// Merged, skinned prop geometry for every entity kind (§29.4 WP5, §11.3, §6): one geometry per kind
// and detail level, shared by all instances. Parts (bones) are listed per kind in `EP`.
import {
  CircleGeometry, CylinderGeometry, DodecahedronGeometry, PlaneGeometry, Sphere,
  SphereGeometry, TorusGeometry, Vector3,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { MODE, PAT, PartGeometryBuilder, type PartGeometry } from '../materials/partGeometry';
import { curve, extrude, helixSpring, M, smoothLathe, starPoints, taperedTube } from '../bean/geo';
import { buildRoyalCrown } from '../bean/hats';

/** Bone indices per prop. Every prop has a body bone 0 and its googly eyes (when it has any) at 1–4. */
export const EP = {
  BODY: 0, EYE_L: 1, EYE_R: 2, PUPIL_L: 3, PUPIL_R: 4,
  // Crate
  FLAP_L: 5, FLAP_R: 6,
  // Bomb
  SPARK: 5,
  // Dummy
  POST: 5, BASE: 6,
  // Plunger rope (ring bones start here)
  ROPE0: 1,
} as const;
export const PROP_BONES = 8;
export const ROPE_RINGS = 12;
export const ROPE_BONES = 1 + ROPE_RINGS;

export type PropKind = 'crate' | 'peel' | 'bomb' | 'plunger' | 'pebble' | 'crown' | 'cube' | 'dummy' | 'rope';

const cache = new Map<string, PartGeometry>();

export function propGeometry(kind: PropKind, lo: boolean): PartGeometry {
  const key = `${kind}:${lo ? 1 : 0}`;
  let g = cache.get(key);
  if (!g) {
    g = build(kind, lo);
    cache.set(key, g);
  }
  return g;
}

function detail(lo: boolean): (n: number) => number {
  return (n) => Math.max(3, Math.round(lo ? n * 0.6 : n));
}

/** Googly eyes on a face at depth z, eye radius r, spacing dx, height y (prop-local, +z = face). */
function googly(b: PartGeometryBuilder, k: (n: number) => number, r: number): void {
  for (const [eye, pupil] of [[EP.EYE_L, EP.PUPIL_L], [EP.EYE_R, EP.PUPIL_R]] as const) {
    b.add(new SphereGeometry(r, k(16), k(12)), null, { part: eye, mode: MODE.EYE, color: '#FFFFFF', outline: 0.8 });
    b.add(new SphereGeometry(r * 0.5, k(12), k(8)), M(0, 0, 0, 0, 0, 0, 1, 1, 0.45), { part: pupil, mode: MODE.PUPIL, outline: 0 });
  }
}

function build(kind: PropKind, lo: boolean): PartGeometry {
  const b = new PartGeometryBuilder();
  const k = detail(lo);
  let bounds = new Sphere(new Vector3(0, 0.5, 0), 1.2);
  switch (kind) {
    case 'crate': {
      // §3.1: a wobbly cardboard box with googly eyes. 0.9 m cube-ish, flaps on top, tape, "?" print.
      const w = 0.92;
      const h = 0.78;
      b.add(new RoundedBoxGeometry(w, h, w, lo ? 1 : 2, 0.06), M(0, h / 2, 0), { part: EP.BODY, mode: MODE.CARDBOARD, color: '#D99A55' });
      // Tape band over the top and down the front/back.
      b.add(new RoundedBoxGeometry(0.2, h + 0.012, w + 0.012, 1, 0.02), M(0, h / 2, 0), { part: EP.BODY, mode: MODE.DECAL, color: '#E9C98F', outline: 0 });
      // Side "?" prints (left/right faces) in toy red.
      for (const side of [-1, 1]) {
        const q = new TorusGeometry(0.12, 0.035, k(6), k(16), Math.PI * 1.35);
        q.rotateZ(-Math.PI * 0.18);
        b.add(q, M(side * (w / 2 + 0.006), h * 0.6, 0, 0, side * Math.PI / 2, 0), { part: EP.BODY, mode: MODE.DECAL, color: '#FF5A4E', outline: 0 });
        b.add(new CylinderGeometry(0.035, 0.035, 0.08, k(8)), M(side * (w / 2 + 0.006), h * 0.43, 0, 0, 0, 0), { part: EP.BODY, mode: MODE.DECAL, color: '#FF5A4E', outline: 0 });
        b.add(new SphereGeometry(0.042, k(10), k(8)), M(side * (w / 2 + 0.01), h * 0.27, 0, 0, 0, 0, 0.5, 1, 1), { part: EP.BODY, mode: MODE.DECAL, color: '#FF5A4E', outline: 0 });
      }
      // Two top flaps (hinged at the top edges), slightly open.
      for (const [part, side] of [[EP.FLAP_L, -1], [EP.FLAP_R, 1]] as const) {
        b.add(new RoundedBoxGeometry(w * 0.5, 0.035, w * 0.94, 1, 0.012), M(-side * w * 0.25, 0, 0), { part, mode: MODE.CARDBOARD, color: '#E3A865' });
      }
      googly(b, k, 0.14);
      bounds = new Sphere(new Vector3(0, 0.45, 0), 1.0);
      break;
    }
    case 'peel': {
      // A splayed banana peel: four floppy petals curling up from a stem, brown tips and freckles.
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.4;
        const petal = taperedTube(curve([[0, 0.12, 0], [0.12, 0.07, 0], [0.24, 0.035, 0], [0.34, 0.05, 0], [0.4, 0.1, 0]]), 0.075, 0.035, lo ? 7 : 11, k(10));
        petal.scale(1, 0.5, 1.45);
        b.add(petal, M(0, 0, 0, 0, a, 0), { part: EP.BODY, mode: MODE.VCOLOR, color: '#FFE14D', color2: '#9A6A35', pat: PAT.DOTS, around: 5, freq: 26 });
        b.add(new SphereGeometry(0.04, k(8), k(6)), M(Math.cos(a) * 0.4, 0.1, -Math.sin(a) * 0.4), { part: EP.BODY, mode: MODE.VCOLOR, color: '#6B3E2E', outline: 0.5 });
      }
      b.add(smoothLathe([[0.0, 0.0], [0.1, 0.02], [0.09, 0.13], [0.06, 0.22], [0.04, 0.3], [0, 0.32]], 8, k(12)), null, { part: EP.BODY, mode: MODE.VCOLOR, color: '#F7D640' });
      b.add(new CylinderGeometry(0.026, 0.034, 0.1, k(8)), M(0.015, 0.35, 0, 0, 0, -0.35), { part: EP.BODY, mode: MODE.VCOLOR, color: '#6B3E2E', outline: 0.6 });
      bounds = new Sphere(new Vector3(0, 0.1, 0), 0.8);
      break;
    }
    case 'bomb': {
      // §11.3 Bonk Bomb: a glossy black bomb, metal cap, curly fuse with a spark, grumpy googly eyes.
      b.add(new SphereGeometry(0.35, k(24), k(16)), null, { part: EP.BODY, mode: MODE.POTATO, color: '#3A2A4A' });
      b.add(new CylinderGeometry(0.11, 0.13, 0.1, k(16)), M(0, 0.35, 0), { part: EP.BODY, mode: MODE.VCOLOR, color: '#9AA9B8' });
      b.add(taperedTube(curve([[0, 0.38, 0], [0.02, 0.48, 0], [0.09, 0.55, 0.02], [0.15, 0.53, 0.05]]), 0.03, 0.024, 12, 6), null, { part: EP.BODY, mode: MODE.VCOLOR, color: '#E0C08A', outline: 0.6 });
      b.add(extrude(starPoints(0.1, 0.04, 6), 0.035, 0.008), null, { part: EP.SPARK, mode: MODE.SPARK, color: '#FFB020', outline: 0 });
      googly(b, k, 0.09);
      // Angry brows.
      for (const side of [-1, 1]) {
        b.add(new RoundedBoxGeometry(0.13, 0.035, 0.03, 1, 0.012), M(side * 0.11, 0.17, 0.31, -0.3, 0, side * 0.4), { part: EP.BODY, mode: MODE.VCOLOR, color: '#1A1020', outline: 0 });
      }
      bounds = new Sphere(new Vector3(0, 0.1, 0), 0.8);
      break;
    }
    case 'plunger': {
      // §3.3 Plunger: red rubber cup facing +z, wooden handle behind it.
      const cup = smoothLathe([[0.0, 0.0], [0.12, 0.02], [0.2, 0.08], [0.24, 0.17], [0.22, 0.18], [0.16, 0.1], [0.06, 0.05], [0.0, 0.05]], 10, k(20));
      b.add(cup, M(0, 0, 0.2, -Math.PI / 2, 0, 0), { part: EP.BODY, mode: MODE.VCOLOR, color: '#FF3B3B' });
      b.add(new CylinderGeometry(0.03, 0.03, 0.55, k(10)), M(0, 0, -0.09, Math.PI / 2, 0, 0), { part: EP.BODY, mode: MODE.VCOLOR, color: '#C98A4B', color2: '#A86B33', pat: PAT.STRIPES, freq: 22, axis: 2 });
      b.add(new SphereGeometry(0.045, k(10), k(8)), M(0, 0, -0.37), { part: EP.BODY, mode: MODE.VCOLOR, color: '#A86B33' });
      bounds = new Sphere(new Vector3(0, 0, 0), 0.6);
      break;
    }
    case 'rope': {
      // The plunger's rope (Verlet, client-only): ring bones from 1.
      b.addChainTube(EP.ROPE0, ROPE_RINGS, 5, { part: EP.BODY, mode: MODE.VCOLOR, color: '#FFF1C9', outline: 0.5 });
      bounds = new Sphere(new Vector3(0, 0, 0), 100);
      break;
    }
    case 'pebble':
      // §2.12: a small rock dropped by a ghost.
      b.add(new DodecahedronGeometry(0.2, lo ? 0 : 1), M(0, 0, 0, 0.3, 0.5, 0, 1, 0.8, 1), { part: EP.BODY, mode: MODE.VCOLOR, color: '#9AA3AE' });
      b.add(new DodecahedronGeometry(0.07, 0), M(0.12, 0.08, 0.06), { part: EP.BODY, mode: MODE.VCOLOR, color: '#B9C2CC', outline: 0.6 });
      bounds = new Sphere(new Vector3(0, 0, 0), 0.4);
      break;
    case 'crown':
      // §4.3 Crown Grab crown, entity-sized (radius 0.4).
      buildRoyalCrown(b, EP.BODY, 2.0, lo ? 0.6 : 1);
      bounds = new Sphere(new Vector3(0, 0.25, 0), 0.7);
      break;
    case 'cube':
      // §6.2 sugar cube: RoundedBox 0.9, radius 0.12, sparkle shader.
      b.add(new RoundedBoxGeometry(0.9, 0.9, 0.9, lo ? 2 : 3, 0.12), M(0, 0.45, 0), { part: EP.BODY, mode: MODE.SUGAR, color: '#FBF7FF' });
      bounds = new Sphere(new Vector3(0, 0.45, 0), 0.9);
      break;
    case 'dummy': {
      // §6.1 punching dummy: a striped bean bag on a spring post, with a target face.
      const bag = smoothLathe([[0, 0.0], [0.3, 0.03], [0.4, 0.15], [0.42, 0.4], [0.38, 0.65], [0.26, 0.82], [0, 0.88]], lo ? 10 : 16, k(24));
      b.add(bag, null, { part: EP.BODY, mode: MODE.VCOLOR, color: '#FF5A4E', color2: '#FFF6E9', pat: PAT.STRIPES, freq: 6.5, axis: 1 });
      // Target painted on the face.
      for (const [r, col] of [[0.2, '#FFF6E9'], [0.14, '#FF5A4E'], [0.075, '#FFF6E9']] as const) {
        const disc = new CircleGeometry(r, k(28));
        b.add(disc, M(0, 0.45, 0.418 + (0.2 - r) * 0.03), { part: EP.BODY, mode: MODE.DECAL, color: col, outline: 0 });
      }
      b.add(new SphereGeometry(0.08, k(12), k(8)), M(0, 0.92, 0, 0, 0, 0, 1, 0.5, 1), { part: EP.BODY, mode: MODE.VCOLOR, color: '#6B3E2E' });
      googly(b, k, 0.08);
      // Spring post and a weighted base (stay put while the bag wobbles).
      b.add(helixSpring(5, 0.08, 0.018), M(0, 0, 0, -Math.PI / 2, 0, 0, 1, 1, 0.32), { part: EP.POST, mode: MODE.VCOLOR, color: '#C7D2DD', outline: 0.5 });
      b.add(new CylinderGeometry(0.34, 0.4, 0.12, k(24)), M(0, 0.06, 0), { part: EP.BASE, mode: MODE.VCOLOR, color: '#5B4A6B' });
      bounds = new Sphere(new Vector3(0, 0.7, 0), 1.2);
      break;
    }
  }
  return b.build(bounds);
}

/** A flat sign quad for the dummy's Dizzy readout (non-skinned; the texture is the sign). */
export function signGeometry(): PlaneGeometry {
  return new PlaneGeometry(0.62, 0.36);
}
